import React, { useState, useEffect } from "react";
import {
    Box,
    Stack,
    Typography,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import WSDateField from "../../Components/DateField/WSDateField";
import { useForm } from "react-hook-form";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import { useParams } from "react-router-dom";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import http from "../../commonFunctions/httpClient";

type SlotGroup = "morning" | "afternoon" | "evening";

interface Slot {
    id: string;
    startTime: string;
    endTime: string;
    isAvailable: boolean;
}

export interface Schedule {
    morning: Slot[];
    afternoon: Slot[];
    evening: Slot[];
}

interface DateScheduleProps {
    profileId?: string
    onChange?: (data: {
        date: string | null;
        slots: Schedule;
        selectedSlots: Schedule;
        unselectedSlots: Schedule
    }) => void;
}

const DateSchedule: React.FC<DateScheduleProps> = ({ onChange, profileId }) => {
    const [selectedDate, setSelectedDate] = useState<string>(

    );
    const { showMessage } = useSnackbar();
    const [schedule, setSchedule] = useState<Schedule>({
        morning: [],
        afternoon: [],
        evening: [],
    });
    const [selectedSlots, setSelectedSlots] = useState<any>({
        morning: [],
        afternoon: [],
        evening: [],
    });
    const { control } = useForm();
    const params = useParams();
    const doctorProfileId = profileId || params?.profileId;

    const fetchSlots = async (date: string) => {
        try {
            const response = await callAPI(
                `DoctorSchedule/doctor/${doctorProfileId}/date/${date}`,
                Method.GET
            );
            if (response === "") {
                showMessage('No slots avialabe', 'error')
            }
            const data: Schedule = response?.slots || {
                morning: [],
                afternoon: [],
                evening: [],
            };

            setSchedule(data);

            // Automatically select all available slots initially
            setSelectedSlots({
                morning: data.morning

                    .sort((a, b) =>
                        dayjs(a.startTime, "HH:mm:ss").isBefore(dayjs(b.startTime, "HH:mm:ss"))
                            ? -1
                            : 1
                    )
                    .map((s) => dayjs(s.startTime, "HH:mm:ss").format("h:mm a")),
                afternoon: data.afternoon

                    .sort((a, b) =>
                        dayjs(a.startTime, "HH:mm:ss").isBefore(dayjs(b.startTime, "HH:mm:ss"))
                            ? -1
                            : 1
                    )
                    .map((s) => dayjs(s.startTime, "HH:mm:ss").format("h:mm a")),
                evening: data.evening
                    .sort((a, b) =>
                        dayjs(a.startTime, "HH:mm:ss").isBefore(dayjs(b.startTime, "HH:mm:ss"))
                            ? -1
                            : 1
                    )
                    .map((s) => dayjs(s.startTime, "HH:mm:ss").format("h:mm a")),
            });
        } catch (error) {
            console.error("Error fetching slots:", error);
            setSchedule({ morning: [], afternoon: [], evening: [] });
            setSelectedSlots({ morning: [], afternoon: [], evening: [] });
        }
    };

    useEffect(() => {
        fetchSlots(selectedDate!);
    }, [selectedDate]);

    // useEffect(() => {
    //     if (onChange) {
    //         onChange({ date: selectedDate!, slots: schedule, selectedSlots });
    //     }
    // }, [selectedDate, schedule, selectedSlots, onChange]);
    useEffect(() => {
        if (onChange) {
            const unselected: any = getUnselectedSlotIds();
            onChange({
                date: selectedDate!,
                slots: schedule,
                selectedSlots,
                unselectedSlots: unselected, // <-- now you also return unselected IDs
            });
        }
    }, [selectedDate, schedule, selectedSlots, onChange]);

    const updateSlots = async (slotId: string, isActive: boolean) => {
        try {
            const response: any = await http.patch(`DoctorSchedule/${slotId}/status`, { isActive: isActive })

            if (response) {
                showMessage(response, 'success')
            }
        } catch (error) {

        }
    }

    const toggleSlot = (group: SlotGroup, time: string, slotId: string) => {
        const isCurrentlySelected = selectedSlots[group].includes(slotId);
        

    // 1️⃣ Update local state immediately
    setSelectedSlots((prev: any) => {
        const updated = { ...prev };
        if (isCurrentlySelected) {
            updated[group] = updated[group].filter((id: string) => id !== slotId);
        } else {
            updated[group] = [...updated[group], slotId];
        }
        return updated;
    });

    // 2️⃣ Call API separately
    updateSlots(slotId, !isCurrentlySelected);

    };
    // Utility: get unselected slots with IDs
    const getUnselectedSlotIds = () => {
        const unselected: { morning: string[]; afternoon: string[]; evening: string[] } = {
            morning: [],
            afternoon: [],
            evening: [],
        };

        (["morning", "afternoon", "evening"] as SlotGroup[]).forEach((group) => {
            schedule[group].forEach((slot) => {
                if (slot.isAvailable) {
                    const formattedStart = dayjs(slot.startTime, "HH:mm:ss").format("h:mm a");
                    if (!selectedSlots[group].includes(formattedStart)) {
                        unselected[group].push(slot.id); // collect unselected IDs
                    }
                }
            });
        });

        return unselected;
    };

    return (
        // <Paper >
        <Box sx={{ p: 2 }}>
            <WSDateField
                label="Select Date"
                id="schedule-date"
                name="scheduleDate"
                control={control}
                rules={{ required: "Date is required" }}
                onChange={(date: any) =>
                    setSelectedDate(dayjs(date).format("YYYY-MM-DD"))
                }
                defaultValue={selectedDate}
                denyFuturOrPast="none"
            />

            <Box sx={{ mt: 4 }}>
                {(["morning", "afternoon", "evening"] as SlotGroup[]).map((group) => (
                    <Accordion key={group} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ textTransform: "capitalize", fontWeight: "bold" }}>
                                {group === "afternoon" ? "After noon" : group === "evening" ? "Evening" : "Morning"}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={1}>
                                {schedule[group]?.map((slot: Slot) => {
                                    const formattedStart = dayjs(slot.startTime, "HH:mm:ss").format(
                                        "h:mm a"
                                    );
                                    const formattedEnd = dayjs(slot.endTime, "HH:mm:ss").format(
                                        "h:mm a"
                                    );
                                    const isSelected =
                                        slot.isAvailable && selectedSlots[group]?.includes(formattedStart);

                                    return (
                                        <Grid item key={slot.id}>
                                            <Chip
                                                label={`${formattedStart} - ${formattedEnd}`}
                                                sx={{
                                                    backgroundColor: slot.isAvailable

                                                        ? isSelected
                                                            ? "#d1e7dd" // selected color (greenish as in your screenshot)
                                                            : "#f8f9fa" // default available color (light gray/white)
                                                        : "#e0e0e0", // unavailable slots
                                                    color: slot.isAvailable ? (isSelected ? "#0f5132" : "#212529") : "#6c757d",
                                                    borderRadius: "8px",
                                                    //  cursor: slot.isAvailable ? "pointer" : "not-allowed",
                                                    "&:hover": {
                                                        backgroundColor: slot.isAvailable
                                                            ? isSelected
                                                                ? "#c7dfd4"
                                                                : "#e9ecef"
                                                            : "#e0e0e0",
                                                    },
                                                }}
                                                // disabled={!slot.isAvailable}
                                                onClick={() =>
                                                    toggleSlot(group, formattedStart, slot?.id)
                                                }
                                            />

                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </Box>
        // </Paper>
    );
};

export default DateSchedule;
